import { useQuery } from "@tanstack/react-query";
import { FileText, Download } from "lucide-react";
import { api } from "@/lib/api";
import { PageLoader, EmptyState } from "@/components/ui";
import { fmtDateTime } from "@/lib/format";
import { useActivities } from "./useActivities";
import type { ProjectBundle } from "../ProjectDetail";

export function Files({ bundle }: { bundle: ProjectBundle }) {
  const { data: activities } = useActivities(bundle.project.id);
  // Aggregate attachments by fetching per-activity detail is heavy; instead we expose
  // a project-wide list from activities that have attachments.
  const withFiles = (activities ?? []).filter((a) => a.attachmentCount > 0);

  const details = useQuery({
    queryKey: ["project-files", bundle.project.id, withFiles.map((a) => a.id).join(",")],
    enabled: withFiles.length > 0,
    queryFn: async () => {
      const results = await Promise.all(withFiles.map((a) => api.get<{ attachments: any[] }>(`/activities/${a.id}`)));
      return results.flatMap((r, i) => r.attachments.map((att) => ({ ...att, activityTitle: withFiles[i].title, activityId: withFiles[i].id })));
    },
  });

  if (withFiles.length === 0) return <EmptyState icon={FileText} title="No files yet" hint="Evidence and deliverables attached to activities appear here." />;
  if (details.isLoading) return <PageLoader />;

  return (
    <div className="card p-0">
      <table className="w-full text-sm">
        <thead><tr>{["File", "Activity", "Kind", "Uploaded by", "When", ""].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
        <tbody>
          {(details.data ?? []).map((f: any) => (
            <tr key={f.id} className="border-b border-line hover:bg-surface-2/40">
              <td className="td flex items-center gap-2"><FileText size={15} className="text-ink-faint" /> {f.filename}</td>
              <td className="td text-ink-soft">{f.activityTitle}</td>
              <td className="td"><span className="chip bg-surface-2 text-ink-soft capitalize">{f.kind}</span></td>
              <td className="td text-ink-soft">{f.uploadedByName}</td>
              <td className="td text-ink-faint">{fmtDateTime(f.createdAt)}</td>
              <td className="td"><a href={`${f.url}?dl=1`} className="btn-ghost !p-1"><Download size={14} /></a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
